import { Button } from '@repo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { Label } from '@repo/ui/label'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { ExternalLinkIcon, MessageSquare } from 'lucide-react'

import { ItemCreatedDate } from '@/components/CollapsibleCard'
import { MarkdownWrapper } from '@/components/MarkdownWrapper'
import { useTrace } from '@/hooks'
import { buildRoute, RouteNames } from '@/routes'
import { Feedback } from '@/store/admin'

export type FeedbacksProps = {
  feedbacks: Feedback[]
  organizationId: string
}

export function Feedbacks({ feedbacks, organizationId }: FeedbacksProps) {
  const { t } = useT({ keyPrefix: 'admin.feedbacks' })
  return (
    <Card className="flex flex-1 flex-col overflow-hidden overflow-y-auto">
      <Header />

      {feedbacks.length === 0 ? (
        <CardContent>{t('noFeedbackYet')}</CardContent>
      ) : (
        <CardContent className="space-y-2">
          {feedbacks.map((feedback) => {
            const chatSessionUrl = buildRoute(RouteNames.Organizations, [
              organizationId,
              RouteNames.Beneficiaries,
              feedback.beneficiaryId,
              RouteNames.BeneficiaryChat,
              feedback.chatSessionId,
            ])
            return (
              <div
                key={feedback.id}
                className="border-primary-50 flex flex-col border-b-8 pb-6 pt-4 first:border-t-8 last:border-b-0"
              >
                <div className="flex items-center justify-between gap-2 pb-4">
                  <div className="flex items-center gap-1.5">
                    <MessageSquare className="text-primary-500 size-4" />{' '}
                    <ItemCreatedDate labelClassName="hidden" createdAt={feedback.createdAt} />
                  </div>
                  <TooltipWrapper content="Open conversation">
                    <Button asChild variant="outline" size="icon">
                      <a href={chatSessionUrl} target="_blank" className="text-xs">
                        <ExternalLinkIcon className="size-4" />
                      </a>
                    </Button>
                  </TooltipWrapper>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="border-primary-100 flex flex-col gap-0.5 rounded-md border p-4 pb-0">
                    <Label className="text-primary-400 flex-1 text-sm">Feedback:</Label>
                    <MarkdownWrapper content={feedback.content} />
                  </div>
                  <div className="border-primary-100 flex flex-col gap-0.5 rounded-md border p-4 pb-0">
                    <div className="flex items-center">
                      <Label className="text-primary-400 flex-1 text-sm">Chat Message:</Label>
                      <Trace traceUrl={feedback.message.traceUrl} />
                    </div>
                    <MarkdownWrapper content={feedback.message.content} />
                  </div>
                </div>
              </div>
            )
          })}
        </CardContent>
      )}
    </Card>
  )
}

function Trace({ traceUrl }: { traceUrl?: string }) {
  const { Trace } = useTrace({ traceUrl })
  return <Trace />
}

function Header() {
  const { t } = useT({ keyPrefix: 'admin.feedbacks' })
  return (
    <CardHeader>
      <CardTitle>{t('title', { cfl: true })}</CardTitle>
      <CardDescription>{t('description')}</CardDescription>
    </CardHeader>
  )
}
